import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthState, User, LoginCredentials, RegisterCredentials } from '../types';

// Base API URL - adjust based on your backend configuration
// In production with subpath deployment, use empty string for relative URLs
const API_BASE_URL = process.env.REACT_APP_API_URL !== undefined
  ? process.env.REACT_APP_API_URL
  : 'http://localhost:8080';

// Global flag to prevent multiple simultaneous logouts
let isLoggingOut = false;

// Global flag and queue for token refresh race condition protection
let isRefreshing = false;
let refreshSubscribers: Array<(token: string | null) => void> = [];

interface ApiResponse<T = any> {
  success?: boolean;
  message?: string;
  detail?: string;
  user?: User;
  access_token?: string;
  refresh_token?: string;
  avatar_url?: string;
  [key: string]: any;
}

interface AuthResponse {
  success: boolean;
  message: string;
  avatar_url?: string;
}

export const getWeComLoginErrorMessage = (data: ApiResponse): string => {
  const errorCode = data.detail || data.message;
  if (errorCode === 'wecom_identity_not_bound') {
    return '该企业微信尚未绑定 Data Intelligence 账号，请联系数据中心开通或绑定账号后重试。';
  }
  return data.message || data.detail || 'Enterprise WeCom login failed';
};

type WeComLoginMode = 'qr' | 'app';

interface AuthStoreActions {
  login: (username: string, password: string) => Promise<AuthResponse>;
  loginWithWeCom: (mode?: WeComLoginMode) => Promise<AuthResponse>;
  exchangeWeComCode: (code: string, state: string) => Promise<AuthResponse>;
  register: (username: string, email: string, password: string) => Promise<AuthResponse>;
  logout: () => Promise<void>;
  refreshAccessToken: (silentFail?: boolean) => Promise<boolean>;
  getCurrentUser: () => Promise<User | null>;
  checkAuth: () => boolean;
  initializeAuth: () => Promise<void>;
  updateProfile: (profileData: Partial<User>) => Promise<AuthResponse>;
  uploadAvatar: (file: File) => Promise<AuthResponse>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResponse>;
  clearError: () => void;
  markWelcomeAnimationSeen: () => void;
}

type AuthStoreType = AuthState & AuthStoreActions;

const useAuthStore = create<AuthStoreType>()(
  persist(
    (set, get) => ({
      // State
      isAuthenticated: false,
      user: null,
      accessToken: null,
      refreshToken: null,
      isLoading: false,
      error: null,
      hasSeenWelcomeAnimation: false,
      isInitialized: false,

      // Actions

      // Login method with real JWT authentication
      login: async (username: string, password: string): Promise<AuthResponse> => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/login`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, password }),
          });

          const data: ApiResponse = await response.json();

          if (response.ok) {
            set({
              isAuthenticated: true,
              user: data.user || null,
              accessToken: data.access_token || null,
              refreshToken: data.refresh_token || null,
              isLoading: false,
              error: null,
              hasSeenWelcomeAnimation: false, // Reset on login
            });
            return { success: true, message: 'Login successful' };
          } else {
            const errorMessage = data.message || data.detail || 'Login failed';
            set({ isLoading: false, error: errorMessage });
            return { success: false, message: errorMessage };
          }
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Start the enterprise WeCom OAuth flow. The backend sets a short-lived
      // HttpOnly state cookie and returns the official WeCom authorization URL.
      loginWithWeCom: async (mode: WeComLoginMode = 'qr'): Promise<AuthResponse> => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(
            `${API_BASE_URL}/api/auth/wecom/start?mode=${encodeURIComponent(mode)}`,
            { credentials: 'include' }
          );
          const data: ApiResponse = await response.json();

          if (response.ok && data.auth_url) {
            window.location.assign(data.auth_url);
            return { success: true, message: 'Redirecting to WeCom...' };
          }

          const errorMessage = data.message || data.detail || 'Enterprise WeCom login is unavailable';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Exchange the one-time WeCom callback code for the same JWT/refresh-token
      // pair used by username/password login.
      exchangeWeComCode: async (code: string, state: string): Promise<AuthResponse> => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/wecom/exchange`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            credentials: 'include',
            body: JSON.stringify({ code, state }),
          });
          const data: ApiResponse = await response.json();

          if (response.ok) {
            set({
              isAuthenticated: true,
              user: data.user || null,
              accessToken: data.access_token || null,
              refreshToken: data.refresh_token || null,
              isLoading: false,
              error: null,
              hasSeenWelcomeAnimation: false,
            });
            return { success: true, message: 'Login successful' };
          }

          const errorMessage = getWeComLoginErrorMessage(data);
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Register method
      register: async (username: string, email: string, password: string): Promise<AuthResponse> => {
        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/register`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ username, email, password }),
          });

          const data: ApiResponse = await response.json();

          if (response.ok) {
            set({ isLoading: false, error: null });
            return { success: true, message: 'Registration successful. Please log in.' };
          } else {
            const errorMessage = data.message || data.detail || 'Registration failed';
            set({ isLoading: false, error: errorMessage });
            return { success: false, message: errorMessage };
          }
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Logout method with token invalidation
      logout: async (): Promise<void> => {
        // Prevent multiple simultaneous logout calls
        if (isLoggingOut) {
          return;
        }

        isLoggingOut = true;
        const { accessToken, refreshToken } = get();

        // Notify Parallax to clear its httpOnly session cookie (fire-and-forget).
        // Parallax's cookie TTL is 30d; without this call, users stay logged in
        // to Parallax for up to 30d after logging out of DatacenterAI.
        // See docs/features/active/parallax-integration/PARALLAX_SSO_SPEC.md
        const parallaxUrl = process.env.REACT_APP_PARALLAX_URL;
        if (parallaxUrl) {
          fetch(`${parallaxUrl}/api/logout`, {
            method: 'POST',
            credentials: 'include',
            mode: 'cors',
          }).catch(() => {
            // Failure must not block local logout.
          });
        }

        const prismUrl = process.env.REACT_APP_PRISM_URL;
        if (prismUrl) {
          fetch(`${prismUrl}/api/logout`, {
            method: 'POST',
            credentials: 'include',
            mode: 'cors',
          }).catch(() => {
            // Failure must not block local logout.
          });
        }

        const shopvistaUrl = process.env.REACT_APP_SHOPVISTA_URL;
        if (shopvistaUrl) {
          fetch(`${shopvistaUrl}/api/logout`, {
            method: 'POST', credentials: 'include', mode: 'cors',
          }).catch(() => {
            // Cross-origin cleanup is best effort and must not block logout.
          });
        }

        const sonarUrl = process.env.REACT_APP_SONAR_URL;
        if (sonarUrl) {
          fetch(`${sonarUrl}/api/logout`, {
            method: 'POST',
            credentials: 'include',
            mode: 'cors',
          }).catch(() => {
            // Failure must not block local logout.
          });
        }

        const cmiUrl = process.env.REACT_APP_CMI_URL;
        if (cmiUrl) {
          fetch(`${cmiUrl}/api/logout`, {
            method: 'POST',
            credentials: 'include',
            mode: 'cors',
          }).catch(() => {
            // Failure must not block local logout.
          });
        }

        const coreDailyReportUrl = process.env.REACT_APP_CORE_DAILY_REPORT_URL;
        if (coreDailyReportUrl) {
          fetch(`${coreDailyReportUrl}/api/logout`, {
            method: 'POST',
            credentials: 'include',
            mode: 'cors',
          }).catch(() => {
            // Failure must not block local logout.
          });
        }

        // Clear chat stores to prevent data leakage between users
        try {
          const { default: useChatStore } = await import('../features/chat/store/chatStore');
          const { default: useChatSessionStore } = await import('../features/chat/store/chatSessionStore');
          const { clearCommandCache } = await import('../features/chat/components/ChatInterface/SlashCommandMenu');

          useChatStore.getState().clearChat();
          useChatSessionStore.getState().resetStore();
          clearCommandCache();
        } catch (error) {
          console.error('[authStore] Failed to clear chat stores:', error);
        }

        // Try to invalidate the refresh token on the server
        if (accessToken && refreshToken) {
          try {
            const response = await fetch(`${API_BASE_URL}/api/auth/logout`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${accessToken}`, // Use accessToken to authenticate
              },
              body: JSON.stringify({ refresh_token: refreshToken }), // Send refreshToken in body
            });

            if (!response.ok) {
              const errorData = await response.json().catch(() => ({}));
              console.error('[authStore] Logout failed on backend:', response.status, errorData);
            }
          } catch (error) {
            console.error('[authStore] Logout error:', error);
            // Continue with local logout even if server logout fails
          }
        }

        // Clear local state - keep isInitialized to prevent login page flicker
        set({
          isAuthenticated: false,
          user: null,
          accessToken: null,
          refreshToken: null,
          error: null,
          hasSeenWelcomeAnimation: false, // Reset on logout
          isInitialized: true, // Keep initialized state to prevent flicker
        });

        // Reset the flag after a short delay
        setTimeout(() => {
          isLoggingOut = false;
        }, 1000);
      },

      // Refresh access token with race condition protection
      refreshAccessToken: async (silentFail: boolean = false): Promise<boolean> => {
        const { refreshToken } = get();

        // Check if refresh token is available
        if (!refreshToken) {
          console.warn('[authStore] Token refresh failed: No refresh token available');
          if (!silentFail) {
            await get().logout();
          }
          return false;
        }

        // If already refreshing, wait for the existing refresh to complete
        if (isRefreshing) {
          return new Promise((resolve) => {
            refreshSubscribers.push((token) => {
              resolve(token !== null);
            });
          });
        }

        isRefreshing = true;

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/refresh`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
            },
            body: JSON.stringify({ refresh_token: refreshToken }),
          });

          const data: ApiResponse = await response.json();

          if (response.ok) {
            const newAccessToken = data.access_token || null;

            set({
              accessToken: newAccessToken,
              // Preserve existing refresh_token if backend doesn't return a new one
              // This prevents token loss during auto-refresh
              refreshToken: data.refresh_token || get().refreshToken,
              error: null,
            });

            // Notify all waiting subscribers with the new token
            refreshSubscribers.forEach((callback) => callback(newAccessToken));
            refreshSubscribers = [];
            isRefreshing = false;

            return true;
          } else {
            // Refresh token is invalid or expired
            console.error(
              '[authStore] Token refresh failed:',
              `Status ${response.status}`,
              data.message || data.detail || 'Unknown error'
            );

            // Notify all waiting subscribers of failure
            refreshSubscribers.forEach((callback) => callback(null));
            refreshSubscribers = [];
            isRefreshing = false;

            // Handle 401 Unauthorized (session revoked/expired)
            // Immediately clear local state without calling backend logout API
            // This prevents repeated failed refresh attempts with revoked tokens
            if (response.status === 401) {
              console.warn(
                '[authStore] Session revoked or expired (401), clearing local state immediately'
              );
              set({
                isAuthenticated: false,
                user: null,
                accessToken: null,
                refreshToken: null,  // Clear the revoked/expired token
                error: null,
                isInitialized: true,  // Keep initialized state to prevent flicker
              });
              return false;
            }

            // For other error status codes, follow the original logout flow
            if (!silentFail) {
              // Only logout on explicit refresh failures (user actions)
              await get().logout();
            }
            return false;
          }
        } catch (error) {
          // Network error or other temporary failure
          console.error('[authStore] Token refresh error:', error);

          // Notify all waiting subscribers of failure
          refreshSubscribers.forEach((callback) => callback(null));
          refreshSubscribers = [];
          isRefreshing = false;

          if (!silentFail) {
            // Only logout on explicit refresh failures (user actions)
            await get().logout();
          }
          return false;
        }
      },

      // Get current user information
      getCurrentUser: async (): Promise<User | null> => {
        const { accessToken } = get();

        if (!accessToken) {
          return null;
        }

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/me`, {
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
          });

          if (response.ok) {
            const userData: ApiResponse = await response.json();
            set({ user: userData.user || null });
            return userData.user || null;
          } else if (response.status === 401) {
            // Token is invalid, try to refresh
            const refreshSuccess = await get().refreshAccessToken();
            if (refreshSuccess) {
              const { accessToken: newAccessToken } = get();
              if (newAccessToken) {
                try {
                  const retryResponse = await fetch(`${API_BASE_URL}/api/auth/me`, {
                    headers: {
                      'Authorization': `Bearer ${newAccessToken}`,
                    },
                  });
                  if (retryResponse.ok) {
                    const userData: ApiResponse = await retryResponse.json();
                    set({ user: userData.user || null });
                    return userData.user || null;
                  }
                } catch (retryError) {
                  console.error('Retry get current user error:', retryError);
                }
              }
            }
          }
        } catch (error) {
          console.error('Get current user error:', error);
        }

        return null;
      },

      // Check if user is authenticated
      checkAuth: (): boolean => {
        const { isAuthenticated, accessToken } = get();
        return isAuthenticated && !!accessToken;
      },

      // Initialize auth state on app start
      initializeAuth: async (): Promise<void> => {
        const { refreshToken, accessToken, user } = get();

        // If we have an access token but no user data, try to get current user
        if (accessToken && !user) {
          await get().getCurrentUser();
        }
        // If we have a refresh token, try to refresh the access token and get user data
        else if (refreshToken) {
          const refreshSuccess = await get().refreshAccessToken();
          if (refreshSuccess) {
            await get().getCurrentUser();
          }
        }

        // Mark as initialized regardless of success/failure
        set({ isInitialized: true });
      },

      // Update user profile
      updateProfile: async (profileData: Partial<User>): Promise<AuthResponse> => {
        const { accessToken } = get();

        if (!accessToken) {
          return { success: false, message: 'Not authenticated' };
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/profile`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify(profileData),
          });

          const data: ApiResponse = await response.json();

          if (response.ok) {
            set(state => ({
              user: state.user ? { ...state.user, ...data } : null,
              isLoading: false,
              error: null,
            }));
            return { success: true, message: 'Profile updated successfully' };
          } else {
            const errorMessage = data.message || data.detail || 'Profile update failed';
            set({ isLoading: false, error: errorMessage });
            return { success: false, message: errorMessage };
          }
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Upload user avatar
      uploadAvatar: async (file: File): Promise<AuthResponse> => {
        const { accessToken } = get();

        if (!accessToken) {
          return { success: false, message: 'Not authenticated' };
        }

        set({ isLoading: true, error: null });

        try {
          const formData = new FormData();
          formData.append('file', file);

          const response = await fetch(`${API_BASE_URL}/api/auth/upload-avatar`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${accessToken}`,
            },
            body: formData,
          });

          const data: ApiResponse = await response.json();

          if (response.ok) {
            set(state => ({
              user: state.user ? { ...state.user, avatar_url: data.avatar_url } : null,
              isLoading: false,
              error: null,
            }));
            return {
              success: true,
              message: data.message || 'Avatar uploaded successfully',
              avatar_url: data.avatar_url
            };
          } else {
            const errorMessage = data.message || data.detail || 'Avatar upload failed';
            set({ isLoading: false, error: errorMessage });
            return { success: false, message: errorMessage };
          }
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Change password
      changePassword: async (currentPassword: string, newPassword: string): Promise<AuthResponse> => {
        const { accessToken } = get();

        if (!accessToken) {
          return { success: false, message: 'Not authenticated' };
        }

        set({ isLoading: true, error: null });

        try {
          const response = await fetch(`${API_BASE_URL}/api/auth/change-password`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${accessToken}`,
            },
            body: JSON.stringify({
              current_password: currentPassword,
              new_password: newPassword,
            }),
          });

          const data: ApiResponse = await response.json();

          if (response.ok) {
            set({ isLoading: false, error: null });
            return { success: true, message: 'Password changed successfully' };
          } else {
            const errorMessage = data.message || data.detail || 'Password change failed';
            set({ isLoading: false, error: errorMessage });
            return { success: false, message: errorMessage };
          }
        } catch (error) {
          const errorMessage = 'Network error. Please check your connection.';
          set({ isLoading: false, error: errorMessage });
          return { success: false, message: errorMessage };
        }
      },

      // Clear error
      clearError: (): void => {
        set({ error: null });
      },

      // Mark welcome animation as seen
      markWelcomeAnimationSeen: (): void => {
        set({ hasSeenWelcomeAnimation: true });
      },
    }),
    {
      name: 'auth-storage',
      partialize: (state) => ({
        isAuthenticated: state.isAuthenticated,
        user: state.user,
        accessToken: state.accessToken,
        refreshToken: state.refreshToken,
        hasSeenWelcomeAnimation: state.hasSeenWelcomeAnimation,
        isInitialized: state.isInitialized,
      }),
    }
  )
);

export default useAuthStore;
